/**
 * Build a square mosaic of N×N PFP tokens, rendered to a single PNG
 * blob. Used as the collection cover so the home tile stays visually
 * distinct from any individual token.
 *
 * Picks the smallest grid that fits all the supplied blobs up to 4×4
 * (1, 2×2, 3×3, 4×4 — i.e. 1, 4, 9 or 16 tiles). Output is the same
 * pixel size as a single token so file sizes stay sane.
 */
export async function buildMosaicCover(
  blobs: Blob[],
  canvasSize: { width: number; height: number },
): Promise<Blob> {
  if (blobs.length === 0) {
    throw new Error("buildMosaicCover: no blobs supplied");
  }
  const grid = pickGridSize(blobs.length);
  const tilesNeeded = grid * grid;
  const picks = pickTiles(blobs, tilesNeeded);

  const canvas = document.createElement("canvas");
  canvas.width = canvasSize.width;
  canvas.height = canvasSize.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("buildMosaicCover: 2d context unavailable");

  const tileW = canvas.width / grid;
  const tileH = canvas.height / grid;
  for (let i = 0; i < picks.length; i++) {
    const bitmap = await createImageBitmap(picks[i]!);
    const x = (i % grid) * tileW;
    const y = Math.floor(i / grid) * tileH;
    ctx.drawImage(bitmap, x, y, tileW, tileH);
    bitmap.close();
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
      "image/png",
    );
  });
}

function pickGridSize(count: number): 1 | 2 | 3 | 4 {
  if (count >= 16) return 4;
  if (count >= 9) return 3;
  if (count >= 4) return 2;
  return 1;
}

function pickTiles(blobs: Blob[], n: number): Blob[] {
  // Evenly-spaced sample so the mosaic is representative of the full
  // collection, not just the first N tokens. With 30 tokens and a 4×4
  // grid, that's tokens 1, 3, 5, 7, … 29.
  if (blobs.length <= n) return blobs;
  const step = blobs.length / n;
  const out: Blob[] = [];
  for (let i = 0; i < n; i++) {
    out.push(blobs[Math.floor(i * step)]!);
  }
  return out;
}
